import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { BasketMenuIcon } from "tsi-common-react/src/apps/checkout/BasketMenuIcon/index";
import { BasketLineVariant } from "tsi-common-react/src/apps/checkout/constants";
import { AccountMenu } from "tsi-common-react/src/apps/customer-account/AccountMenu/index";
import { CountdownTimer } from "tsi-common-react/src/components/CountdownTimer/index";
import { Link } from "tsi-common-react/src/components/Link/index";
import { Messages } from "tsi-common-react/src/components/Message/index";
import { RichNavigation } from "tsi-common-react/src/components/RichNavigation/index";
import { ValueProps } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";
import { RichText } from "tsi-common-react/src/components/RichText/index";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { ValuePropsBarBlock } from "tsi-common-react/src/components/ValuePropsBarBlock/index";
import { useMobileMenuDialog } from "tsi-common-react/src/hooks/useMobileMenuDialog";
import { ICountdownTimer } from "tsi-common-react/src/models/countdown-timer";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { RICH_NAVIGATION_MOBILE_PANEL_ID } from "tsi-common-react/src/models/rich-navigation";
import {
    isPageInsideIFrame as isPageInsideIFrameCheck,
    usePageSetting,
} from "tsi-common-react/src/utils/settings";
import { useIsSSR } from "tsi-common-react/src/utils/ssr";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context, ValuePropBarBlock, reverse, templates } from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { FinancingModalTriggerMinicart } from "../../apps/checkout/FinancingModalTriggerMinicart";
import { useHeaderBreakpoint } from "../../utils/useHeaderBreakpoint";
import { DesktopRichNav } from "../DesktopRichNav";
import { MainMenu } from "../MainMenu";
import { IMenuItem } from "../MainMenu/menu.interface";

// The sale2 header receives the `chrome` context processor's
// `sale2_site_header` props (non-null on the sale2 deployment; PageSkeleton
// branches on it) plus the per-page flash `messages` PageSkeleton threads in.
type Sale2SiteHeaderProps = NonNullable<
    React.ContextType<typeof Context>["chrome"]["sale2_site_header"]
>;

// The `promo` block value, derived from the promo_header StreamField union
// rather than the reactivated anonymous ``CmsN`` name (whose numbering is not
// stable across template-set changes).
type PromoBlockValue = NonNullable<
    Sale2SiteHeaderProps["promo_settings"]["promo_header"]
>["value"][number]["value"];

type ValuePropsBarBlockContainerProps = Immutable<{
    id?: string;
    type?: "value_prop_bar";
    index?: number;
    value: ValuePropBarBlock;
}>;

type PromoHeaderBlockProps = Immutable<{
    id?: string;
    type?: "promo";
    index?: number;
    value: PromoBlockValue;
}>;

const PromoHeaderBlock = ({ value }: PromoHeaderBlockProps) => (
    <section>
        <div className="search-nav__promo">
            <RichText html={value.copy ?? ""} />
        </div>
        <div className="search-nav__promo--mobile">
            {value.mobile_copy ? (
                <RichText html={value.mobile_copy ?? ""} />
            ) : (
                <RichText html={value.copy ?? ""} />
            )}
        </div>
    </section>
);

// Adapter to match StreamField block signature to ValuePropsBarBlock's expected props
const ValuePropsBarBlockContainer = ({
    value,
}: ValuePropsBarBlockContainerProps) => {
    // Ensure value_props is always an array and filter out null values
    // Also filter out null values from other properties
    const normalized = {
        value_props: Array.isArray(value.value_props)
            ? value.value_props.filter(
                  (prop): prop is NonNullable<typeof prop> => prop !== null,
              )
            : [],
        spacing: value.spacing ?? undefined,
        background_style: value.background_style ?? undefined,
        responsive_visibility: value.responsive_visibility ?? undefined,
    } as ValuePropBarBlock;

    return (
        <section className="block-value_prop_bar">
            <ValuePropsBarBlock valuePropsData={normalized} />
        </section>
    );
};

type IProps = Sale2SiteHeaderProps & {
    messages: templates.UtilityPageTemplate["page_chrome"]["messages"];
};

export const Sale2SiteHeader = (props: IProps) => {
    const isSSR = useIsSSR();
    const viewport = useHeaderBreakpoint();
    const {
        promo_settings,
        messages,
        rich_navigation,
        desktop_rich_navigation,
        top_nav_menu,
        is_enabled,
        timer_queue,
    } = props;

    const {
        siteHeaderRef,
        mobilePanelVisible,
        showMobileMenuDialog,
        openMobilePanel,
        closeMobilePanel,
    } = useMobileMenuDialog({
        belowMobile: viewport.belowMobile,
        hasRichNavigation: !!rich_navigation,
    });

    const homeURL = usePageURL("home");
    const enableBasketPrequalSDK = usePageSetting("show_prequal_cart") || false;
    const basketURL = isoWebPageURL.wrap(reverse("basket:summary"));
    const checkoutURL = isoWebPageURL.wrap(reverse("checkout:index"));
    // Always false during SSR to ensure consistent hydration
    const isPageInsideIFrame = !isSSR && isPageInsideIFrameCheck();
    const tempurpedicHomeString = t`Tempur-Pedic home`;

    // Map top_nav_menu data to IMenuItem[]
    const menuItems: IMenuItem[] = Array.isArray(top_nav_menu?.menu?.value)
        ? (top_nav_menu.menu.value as IMenuItem[])
        : [];

    // As the StreamFields converted into strings
    // Rename the fields for aliasing and type compatibility
    const timersConvertedType = Array.isArray(timer_queue?.timers)
        ? timer_queue.timers.map((time) => {
              const { body_str, mobile_body_str, timer_theme, ...rest } = time;
              return {
                  ...rest,
                  body: body_str,
                  mobile_body: mobile_body_str,
                  theme: timer_theme,
              } as ICountdownTimer;
          })
        : [];

    const mobileRichNavigation =
        showMobileMenuDialog && rich_navigation ? rich_navigation : null;

    const searchNavClasses = classNames({
        "search-nav": true,
        "search-nav--timer-present": is_enabled,
    });

    return (
        <>
            {promo_settings.show_promo_header && (
                <nav
                    className={searchNavClasses}
                    aria-label={t`Promotional header`}
                >
                    <div className="l-max-width">
                        <a className="screen-reader-skip" href="#main-content">
                            {t`Skip to main content`}
                        </a>
                        {!timer_queue?.is_active ? (
                            <>
                                {promo_settings?.promo_header && (
                                    <StreamField
                                        tagName={React.Fragment}
                                        value={
                                            promo_settings.promo_header.value
                                        }
                                        components={{
                                            promo: PromoHeaderBlock,
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <section className="countdown-timer-container">
                                <CountdownTimer
                                    timerData={timersConvertedType}
                                    // promo_copy was never passed as a prop to include function in the Sale2 PageNavHeader,
                                    // so timersExpiredPromo is always set to an empty string by default.
                                    timersExpiredPromo={""}
                                />
                            </section>
                        )}
                    </div>
                </nav>
            )}

            {messages && <Messages messages={messages} />}

            <header
                className="site-header"
                ref={siteHeaderRef}
                role={showMobileMenuDialog ? "dialog" : undefined}
                aria-modal={showMobileMenuDialog ? true : undefined}
                aria-labelledby={
                    showMobileMenuDialog ? "mobile-panel-title" : undefined
                }
            >
                {showMobileMenuDialog && (
                    <span
                        id="mobile-panel-title"
                        className="ada-screenreader-only"
                    >
                        {t`Menu`}
                    </span>
                )}
                <nav
                    className="site-nav l-max-width"
                    aria-label={t`Site navigation`}
                >
                    <div>
                        {Boolean(rich_navigation) && (
                            <button
                                type="button"
                                className={classNames("mobile-toggle", {
                                    open: mobilePanelVisible,
                                })}
                                onClick={
                                    mobilePanelVisible
                                        ? closeMobilePanel
                                        : (event) =>
                                              openMobilePanel(
                                                  event.currentTarget,
                                              )
                                }
                                aria-controls={
                                    mobilePanelVisible
                                        ? RICH_NAVIGATION_MOBILE_PANEL_ID
                                        : undefined
                                }
                                aria-label={
                                    mobilePanelVisible
                                        ? t`Close menu`
                                        : t`Open menu`
                                }
                                suppressHydrationWarning
                            >
                                <span aria-hidden="true">
                                    {mobilePanelVisible ? (
                                        <SVGIcon name="xIcon" />
                                    ) : (
                                        <SVGIcon name="barsIcon" />
                                    )}
                                </span>
                            </button>
                        )}
                        <h1>
                            <Link
                                href={homeURL}
                                className="site-nav__logo-link"
                                target={isPageInsideIFrame ? "_top" : undefined}
                                suppressHydrationWarning
                            >
                                <SVGIcon
                                    name="sale2Logo"
                                    title={tempurpedicHomeString}
                                    className="site-nav__logo"
                                />
                            </Link>
                        </h1>
                        <div
                            className={classNames({
                                "main-menu-dropdown": true,
                                "rich-navigation--active":
                                    Boolean(rich_navigation),
                            })}
                            role="navigation"
                            aria-label={t`Main menu`}
                        >
                            {desktop_rich_navigation &&
                            !viewport.belowMobile ? (
                                <DesktopRichNav {...desktop_rich_navigation} />
                            ) : (
                                <MainMenu menuItems={menuItems} />
                            )}
                        </div>
                    </div>

                    <ul>
                        <li>{!isSSR && <AccountMenu />}</li>
                        <li>
                            <BasketMenuIcon
                                basketURL={basketURL}
                                checkoutURL={checkoutURL}
                                isPageInsideIFrame={isPageInsideIFrame}
                                basketLineVariant={
                                    BasketLineVariant.MINIMAL_ENHANCED
                                }
                                buildPromoComponents={(closeModal) => {
                                    if (!enableBasketPrequalSDK) {
                                        return null;
                                    }
                                    return (
                                        <FinancingModalTriggerMinicart
                                            closeModal={closeModal}
                                        />
                                    );
                                }}
                                isSSR={isSSR}
                            />
                        </li>
                    </ul>
                </nav>

                {mobileRichNavigation && (
                    <div
                        id={RICH_NAVIGATION_MOBILE_PANEL_ID}
                        className="rich-navigation"
                    >
                        <RichNavigation
                            suppressNavLandmark
                            openMobileContentDefaultState
                            {...mobileRichNavigation}
                        />
                        <ValueProps
                            value_props={mobileRichNavigation.value_props}
                            enableValuePropsOnMobile
                        />
                    </div>
                )}
                {/** Wait clientside css are fully loaded */}
                {!isSSR &&
                    Array.isArray(promo_settings.value_props?.value) &&
                    promo_settings.value_props.value.length > 0 && (
                        <StreamField
                            tagName={React.Fragment}
                            value={promo_settings.value_props?.value ?? []}
                            components={{
                                value_prop_bar: ValuePropsBarBlockContainer,
                            }}
                        />
                    )}
            </header>
        </>
    );
};
